import { useTranslation } from "@msuite/ui/localization";
import { formatIban } from "@msuite/utils/strings";
import type { IncomingInvoiceEventListItem } from "@msuite/utils/types";
import { Link } from "react-router";
import { UserCard } from "@/components/cards";
import {
	formatInvoiceAmount,
	formatInvoiceDate,
	INCOMING_INVOICE_CHECK_LABEL,
	IncomingInvoiceCheckBadge,
	IncomingInvoiceStateTag,
	reminderLevelLabel,
} from "@/components/incoming-invoices";

/** Props Interface */
interface IncomingInvoiceTimelineItemDetailsProps {
	event: IncomingInvoiceEventListItem;
}

type InvoiceState = Parameters<typeof IncomingInvoiceStateTag>[0]["state"];
type CheckState = Parameters<typeof IncomingInvoiceCheckBadge>[0]["state"];

/**
 * What an event carried beyond its sentence: the people asked, the checks that moved, the states
 * a change went between, a decision's comment, a Kürzung's amounts and reason, the delivery
 * note a link names, the fields an update touched, a reading's error.
 */
export function IncomingInvoiceTimelineItemDetails({
	event,
}: IncomingInvoiceTimelineItemDetailsProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const { payload } = event;
	const items = payload.items ?? [];

	/** Render */
	if (event.type === "approval_requested" || event.type === "approval_withdrawn") {
		return (
			<div className="h-stack gap-2 flex-wrap">
				{items.map((item) =>
					item.userId ? (
						<div
							key={item.userId}
							className="h-stack items-center gap-1"
						>
							<UserCard
								userId={item.userId}
								variant="plain"
							/>
							{item.assignment === "assigned" && (
								<span className="label">
									{t(
										item.isSoleApprover
											? "incoming_invoices.handover.sole"
											: "incoming_invoices.handover.handed_over",
									)}
								</span>
							)}
						</div>
					) : null,
				)}
			</div>
		);
	}
	if (event.type === "checked") {
		return (
			<ul className="v-stack gap-1">
				{items.map((item) =>
					item.checkKey ? (
						<li
							key={item.checkKey}
							className="h-stack items-center gap-2"
						>
							<IncomingInvoiceCheckBadge state={(item.state ?? "unchecked") as CheckState} />
							<span className="label">{t(INCOMING_INVOICE_CHECK_LABEL[item.checkKey])}</span>
						</li>
					) : null,
				)}
			</ul>
		);
	}
	if (event.type === "state_changed" && payload.from && payload.to) {
		return (
			<div className="h-stack items-center gap-2">
				<IncomingInvoiceStateTag state={payload.from as InvoiceState} />
				<span className="label">→</span>
				<IncomingInvoiceStateTag state={payload.to as InvoiceState} />
			</div>
		);
	}
	if ((event.type === "approved" || event.type === "rejected") && payload.comment) {
		return <p className="label">„{payload.comment}“</p>;
	}
	if (event.type === "amount_reduced") {
		const currency = payload.currency ?? "EUR";
		return (
			<div className="v-stack gap-1">
				<p className="label">
					{t("incoming_invoices.decision.reduced_from", {
						from: formatInvoiceAmount(toAmount(payload.from), currency),
						to: formatInvoiceAmount(toAmount(payload.to), currency),
					})}
				</p>
				{payload.comment && <p className="label">„{payload.comment}“</p>}
			</div>
		);
	}
	if (event.type === "reduction_lifted") {
		const currency = payload.currency ?? "EUR";
		return (
			<p className="label">
				{formatInvoiceAmount(toAmount(payload.from), currency)} →{" "}
				{formatInvoiceAmount(toAmount(payload.to), currency)}
			</p>
		);
	}
	if (
		(event.type === "delivery_note_linked" || event.type === "delivery_note_unlinked") &&
		payload.number
	) {
		return <p className="label">{payload.number}</p>;
	}
	if (event.type === "field_changed" && payload.fields?.length) {
		return <p className="label">{payload.fields.join(", ")}</p>;
	}
	// The other invoice of a duplicate, as a link: the original it repeats, or the copy merged in.
	const otherId = payload.duplicateOfId ?? payload.incomingInvoiceId;
	if (
		(event.type === "duplicate_detected" ||
			event.type === "duplicate_merged" ||
			event.type === "merged_into") &&
		otherId
	) {
		return (
			<Link
				to={`/controlling/incoming-invoices/${otherId}`}
				className="link label"
			>
				{t("incoming_invoices.events.open_other_invoice")}
			</Link>
		);
	}
	if (event.type === "reminder_received") {
		return (
			<p className="label">
				{t(reminderLevelLabel(payload.level ?? null))} ·{" "}
				{t("incoming_invoices.reminders.due_fee", {
					dueDate: formatInvoiceDate(payload.dueDate ?? null),
					fee: formatInvoiceAmount(payload.fee ?? 0, "EUR"),
				})}
			</p>
		);
	}
	if (event.type === "payment_exported" || event.type === "payment_reverted") {
		const currency = payload.currency ?? "EUR";
		return (
			<p className="label">
				{formatInvoiceAmount(payload.amount ?? null, currency)}
				{payload.skontoAmount
					? ` · ${t("incoming_invoices.payments.skonto_taken")} ${formatInvoiceAmount(payload.skontoAmount, currency)}`
					: null}
			</p>
		);
	}
	if (event.type === "iban_confirmed" && payload.iban) {
		return <p className="label">{formatIban(payload.iban)}</p>;
	}
	if (event.type === "read_failed" && payload.error) {
		return <p className="label">{payload.error}</p>;
	}
	return null;
}

/** An amount of a payload as a number: jsonb carries the numeric columns as numbers already. */
function toAmount(value: string | number | null | undefined) {
	if (value === null || value === undefined) return null;
	const amount = Number(value);
	return Number.isFinite(amount) ? amount : null;
}
